import type { TCacheEntry } from '@casumo/fabric-fundamentals';
import { BaseCache } from './BaseCache';

export class IndexedDBCache extends BaseCache {
    private readonly DB_NAME = 'gatekeeper-fetch-cache';
    private readonly DB_VERSION = 2;
    private readonly STORE_NAME = 'responses';
    private db: IDBDatabase | null = null;

    constructor() {
        super();
        this.initDB();
    }

    private async initDB(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.STORE_NAME)) {
                    db.createObjectStore(this.STORE_NAME);
                }
            };
        });
    }

    private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
        await this.initDB();
        if (!this.db) throw new Error('Database not initialized');
        const transaction = this.db.transaction([this.STORE_NAME], mode);
        return transaction.objectStore(this.STORE_NAME);
    }

    async setStorage(key: string, entry: TCacheEntry): Promise<void> {
        const store = await this.getStore('readwrite');
        store.put(entry, key);
    }

    async getStorage(key: string): Promise<TCacheEntry | undefined> {
        const store = await this.getStore('readonly');
        return new Promise((resolve, reject) => {
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result as TCacheEntry | undefined);
            request.onerror = () => reject(request.error);
        });
    }

    async deleteStorage(key: string): Promise<void> {
        const store = await this.getStore('readwrite');
        store.delete(key);
    }

    async clearStorage(): Promise<void> {
        const store = await this.getStore('readwrite');
        store.clear();
    }

    async getAllEntries(): Promise<[string, TCacheEntry][]> {
        const store = await this.getStore('readonly');
        return new Promise((resolve, reject) => {
            const entries: [string, TCacheEntry][] = [];
            const request = store.openCursor();
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor) {
                    entries.push([cursor.key as string, cursor.value as TCacheEntry]);
                    cursor.continue();
                } else {
                    resolve(entries);
                }
            };
            request.onerror = () => reject(request.error);
        });
    }
}
